import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import MythLabsIcon from './MythLabsIcon';
import './Navbar.css';

function Navbar() {
  const navRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (navRef.current) {
      const navItems = navRef.current.querySelectorAll('.nav-item');
      
      // Set initial state
      gsap.set(navItems, {
        y: 40,
        opacity: 0,
        scale: 0.95
      });

      // Animate in
      gsap.to(navItems, {
        y: 0,
        opacity: 1,
        scale: 1,
        duration: 0.8,
        stagger: 0.08,
        delay: 0.2,
        ease: "power2.out"
      });

      // Smooth scroll for anchor links
      const handleNavClick = (e) => {
        const href = e.target.closest('a')?.getAttribute('href');
        if (href && href.startsWith('/#')) {
          e.preventDefault();
          const targetId = href.substring(2);
          
          // If we're not on the home page, navigate first
          if (location.pathname !== '/') {
            navigate('/');
            // Wait for navigation and DOM update, then scroll
            setTimeout(() => {
              scrollToSection(targetId);
            }, 300);
          } else {
            scrollToSection(targetId);
          }
        }
      };

      const scrollToSection = (targetId) => {
        const offsetOverrides = {
          services: 40, // show section higher
          team: -40,    // scroll a bit further so cards are visible
        };
        if (targetId === 'home') {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }

        // Try multiple times in case DOM hasn't updated yet
        let attempts = 0;
        const tryScroll = () => {
          const targetElement = document.getElementById(targetId);
          if (targetElement) {
            const baseOffset = 80; // default offset for navbar
            const adjustment = offsetOverrides[targetId] ?? 0;
            const offset = baseOffset + adjustment;
            const elementPosition = targetElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          } else if (attempts < 5) {
            attempts++;
            setTimeout(tryScroll, 100);
          }
        };
        tryScroll();
      };

      const navLinks = navRef.current.querySelectorAll('.nav-link');
      navLinks.forEach(link => {
        link.addEventListener('click', handleNavClick);
      });

      return () => {
        navLinks.forEach(link => {
          link.removeEventListener('click', handleNavClick);
        });
      };
    }
  }, [navigate, location.pathname]);

  const handleLogoClick = (e) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 150);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="hero-section">
      <nav className="navbar" ref={navRef}>
        <Link to="/" className="logo" onClick={handleLogoClick}>
          <MythLabsIcon className="logo-icon" />
          <span className="logo-text">MythLabs</span>
        </Link>
        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className={isMobileMenuOpen ? 'hamburger open' : 'hamburger'}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
        <div className={`nav-container ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="nav-item">
            <a href="/#home" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
              <span className="nav-text">
                <span className="nav-text-inner">Home</span>
                <span className="nav-text-clone">Home</span>
              </span>
            </a>
          </div>
          <div className="nav-item">
            <a href="/#services" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
              <span className="nav-text">
                <span className="nav-text-inner">Services</span>
                <span className="nav-text-clone">Services</span>
              </span>
            </a>
          </div>
          <div className="nav-item">
            <a href="/#team" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
              <span className="nav-text">
                <span className="nav-text-inner">Our Team</span>
                <span className="nav-text-clone">Our Team</span>
              </span>
            </a>
          </div>
          <div className="nav-item">
            <a href="/#contact-form" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>
              <span className="nav-text">
                <span className="nav-text-inner">Contact Form</span>
                <span className="nav-text-clone">Contact Form</span>
              </span>
            </a>
          </div>
        </div>
      </nav>
    </div>
  );
}

export default Navbar;

